import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";
import { getGuideAccent } from "../../utils/guideAccent";

// Template318 (Edimburgo, copia de 118): Notting Hill (Page3/Page6 guia-londres)
// Todo desde backend: page.texts[0]=header, texts[1]=heroTitle, texts[2]=intro, sections[0..3]=nottingHill, listBlock, comoLlegar, tooltipBlock
const Template318 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const headerText = page.texts?.[0]?.content;
	const logoImage =
		page.canvasImages?.find((img) => img.id === "logo")?.imageUrl ||
		"/images/logo.png";
	const heroTitle = page.texts?.[1]?.content;
	const introText = page.texts?.[2]?.content;

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found) return found.src ?? found.url;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const nottingHill = sections[0];
	const listBlock = sections[1];
	const comoLlegar = sections[2];
	const tooltipBlock = sections[3];

	const heroImage = getImageSrc("notting-hill");
	const nottingHillSticker = getCanvasImageUrl("notting-hill-sticker");
	const portobelloImage = getImageSrc(nottingHill?.imageId ?? "portobello");
	const portobelloSticker = getCanvasImageUrl(
		tooltipBlock?.stickerCanvasId ?? "portobello-sticker",
	);

	const actualHeaderColor = headerBackgroundColor || "#de5624";
	const accent = getGuideAccent(actualHeaderColor);
	const contentColor = textColor || "#444";

	const h3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const p = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			{/* CABECERA */}
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontSize: "14px",
						fontWeight: 600,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 99999,
					}}
				>
					{headerText}
					{logoImage && (
						<Box
							component="img"
							src={logoImage}
							alt="logo"
							sx={{
								width: isSmallScreen ? 66 : 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			{/* HERO */}
			<Box data-block-id="image-0" sx={{ position: "relative" }}>
				{heroImage && (
					<img
						src={heroImage}
						alt=""
						style={{ width: "100%", height: 200, objectFit: "cover" }}
					/>
				)}
				{nottingHillSticker && (
					<img
						src={nottingHillSticker}
						alt=""
						style={{ width: 90, position: "absolute", bottom: 60, left: 20 }}
					/>
				)}
				{heroTitle && (
					<Box
						component="h1"
						sx={{
							fontSize: "32px",
							position: "absolute",
							bottom: 0,
							left: "20px",
							color: "#fff",
							textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
							margin: "10px",
						}}
					>
						{heroTitle}
					</Box>
				)}
			</Box>

			<Box
				sx={{
					padding: "10px 12px",
					flex: 1,
					position: "relative",
				}}
			>
				{introText && (
					<Box
						component="p"
						sx={{
							fontSize: 13,
							marginTop: "-10px",
							color: "#333",
							marginBottom: "12px",
						}}
					>
						{introText}
					</Box>
				)}

				{/* Notting Hill block */}
				{(nottingHill?.title || nottingHill?.body) && (
					<Box data-block-id="text-1" sx={{ display: "flex", gap: "16px", position: "relative" }}>
						{portobelloImage && (
							<img
								src={portobelloImage}
								alt={nottingHill?.title}
								style={{
									width: "40%",
									borderRadius: 12,
									objectFit: "cover",
									height: 110,
								}}
							/>
						)}
						<Box sx={{ width: "60%" }}>
							{nottingHill?.title && (
								<Box component="h3" sx={h3}>
									{nottingHill.title}
								</Box>
							)}
							{nottingHill?.body && (
								<Box component="p" sx={p}>
									{nottingHill.body}
								</Box>
							)}
						</Box>
						<Box sx={{ position: "absolute", top: 20, left: "34%" }}>
							{nottingHill?.sabiasQue?.[0] && (
								<SabiasQue
									icono={nottingHill.sabiasQue[0].icono}
									texto={nottingHill.sabiasQue[0].texto}
								/>
							)}
						</Box>
					</Box>
				)}

				{/* List block (SabiasQue + listItems) */}
				{(listBlock?.sabiasQue?.length > 0 ||
					listBlock?.listItems?.length > 0) && (
					<Box data-block-id="text-2" sx={{ position: "relative" }}>
						<Box
							sx={{
								position: "absolute",
								top: isSmallScreen ? -40 : -40,
								left: isSmallScreen ? "33%" : "34%",
							}}
						>
							{listBlock?.sabiasQue?.[0] && (
								<SabiasQue
									icono={listBlock.sabiasQue[0].icono}
									texto={listBlock.sabiasQue[0].texto}
								/>
							)}
						</Box>
						{listBlock?.listItems?.length > 0 && (
							<Box
								component="ul"
								sx={{
									fontSize: 13,
									paddingLeft: "20px",
									color: contentColor,
									marginBottom: "12px",
									position: "relative",
								}}
							>
								{listBlock.listItems.map((item, idx) => (
									<Box component="li" key={idx} sx={{ marginBottom: "8px" }}>
										{item.title && <strong>{item.title}</strong>} {item.body}
									</Box>
								))}
							</Box>
						)}
					</Box>
				)}

				{/* Cómo llegar */}
				{(comoLlegar?.title || comoLlegar?.listItems?.length > 0) && (
					<Box
						data-block-id="text-3"
						sx={{
							backgroundColor: accent.boxBg,
							border: `3px solid ${accent.boxBorder}`,
							borderRadius: "12px",
							padding: "8px 14px",
							fontSize: 12,
							color: "#333",
							marginTop: isSmallScreen ? "90px" : 0,
						}}
					>
						{comoLlegar?.title && (
							<Box
								component="h4"
								sx={{
									margin: 0,
									marginBottom: "8px",
									fontSize: 13,
									fontWeight: 700,
									display: "flex",
									alignItems: "center",
									gap: 6,
									color: accent.accent,
									textTransform: "uppercase",
								}}
							>
								{comoLlegar.title}
							</Box>
						)}
						{comoLlegar?.listItems?.length > 0 && (
							<Box
								component="ul"
								sx={{ paddingLeft: "20px", margin: 0, lineHeight: 1.5 }}
							>
								{comoLlegar.listItems.map((item, idx) => (
									<Box component="li" key={idx}>
										{item.title && <strong>{item.title}</strong>} {item.body}
									</Box>
								))}
							</Box>
						)}
					</Box>
				)}

				{/* Tooltip + sticker */}
				{(tooltipBlock?.title || tooltipBlock?.body) && (
					<>
						{portobelloSticker && (
							<img
								src={portobelloSticker}
								alt=""
								style={{
									width: 100,
									position: "absolute",
									bottom: isSmallScreen ? 70 : 10,
									right: 0,
								}}
							/>
						)}
						<Box
							sx={{
								position: "absolute",
								bottom: isSmallScreen ? 100 : 30,
								right: isSmallScreen ? 80 : 63,
								backgroundColor: accent.tooltipBg,
								border: "2px solid #f5c400",
								borderRadius: "16px 16px 0px 16px",
								padding: "10px 12px",
								fontSize: 11,
								color: contentColor,
								boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
								maxWidth: isSmallScreen ? 220 : 180,
							}}
						>
							{tooltipBlock?.title && <strong>{tooltipBlock.title}</strong>}
							{tooltipBlock?.title && tooltipBlock?.body && <br />}
							{tooltipBlock?.body}
						</Box>
					</>
				)}
			</Box>
		</Box>
	);
};

Template318.thumbnail = "/images/template-1.png";

export default Template318;
